/**
 * Campagne d'évaluation nationale (plan « une évaluation par niveau », décision fondateur D9 du 30/10/2026).
 *
 * Le back range une seule évaluation « Evaluation Nationale » par établissement, niveau, matière et année :
 * la campagne. La page d'une classe et la vue d'un groupe la reçoivent découpée par classe (`classe` et
 * `classe_nom` renseignés, comme une évaluation par classe). La vue du chef et du délégué (mode
 * établissement) la reçoit telle quelle : `classe` et `classe_nom` vides, `niveau` renseigné, et chaque
 * copie porte la classe du jour de son élève.
 *
 * Tant que les données locales n'ont pas été fusionnées, les évaluations par classe d'avant coexistent : ces
 * fonctions lisent les deux formes. Une évaluation antérieure (« Evaluation Nationale - Session 2024 »,
 * page Évolution) et un bilan ne sont jamais une campagne.
 */

export const TITRE_EVALUATION_NATIONALE = 'Evaluation Nationale';

const normaliser = (texte) => (texte || '').toString().toLowerCase().trim();

/** Une campagne servie telle quelle (vue du chef) : le titre national, sans classe, avec un niveau. */
export const estCampagne = (evaluation) =>
    evaluation?.titre === TITRE_EVALUATION_NATIONALE && !evaluation.classe && Boolean(evaluation.niveau);

/**
 * Le niveau (« 6 », « 5 », « 4 ») d'une évaluation : celui de la campagne, sinon le premier caractère de sa
 * classe. Null pour une évaluation sans classe ni niveau (année antérieure, bilan d'un groupe).
 */
export const niveauDe = (evaluation) =>
    evaluation?.niveau || (evaluation?.classe ? evaluation.classe_nom?.[0] : null) || null;

/**
 * Les classes qui ont des résultats nationaux, parmi `evaluations` : le nom d'une évaluation par classe, les
 * classes du niveau qu'on trouve dans les copies d'une campagne. Les bilans et les années antérieures ne
 * comptent pas : un QCM de suivi ne fait pas d'une classe une classe importée (défaut 12 du rapport).
 * `niveau` et `matiere` filtrent quand on les donne.
 */
export const classesAvecCopies = (evaluations, { niveau, matiere } = {}) => {
    const classes = new Set();
    (Array.isArray(evaluations) ? evaluations : []).forEach((evaluation) => {
        if (evaluation?.titre !== TITRE_EVALUATION_NATIONALE) return;
        if (matiere && normaliser(evaluation.matiere) !== normaliser(matiere)) return;
        if (niveau && niveauDe(evaluation) !== niveau) return;
        if (estCampagne(evaluation)) {
            // Un élève parti dans un autre niveau garde sa copie, mais sa classe n'est pas une classe du niveau.
            (evaluation.eleves_reponses || []).forEach((ligne) => {
                if (ligne?.classe_nom?.startsWith(evaluation.niveau)) classes.add(ligne.classe_nom);
            });
        } else if (evaluation.classe_nom) {
            classes.add(evaluation.classe_nom);
        }
    });
    return classes;
};

/**
 * La vue niveau du chef : les évaluations réduites aux classes cochées. Une évaluation par classe est gardée
 * si sa classe est cochée. Une campagne l'est si c'est celle du niveau choisi et qu'il lui reste des copies
 * des classes cochées ; sans ce tri par niveau, la fusion par titre prendrait les questions de la première
 * campagne venue, 5e ou 4e.
 */
export const restreindreAuxClasses = (evaluations, nomsClasses, niveau) => {
    const cochees = new Set(nomsClasses);
    return (Array.isArray(evaluations) ? evaluations : []).flatMap((evaluation) => {
        if (!estCampagne(evaluation)) {
            return cochees.has(evaluation?.classe_nom) ? [evaluation] : [];
        }
        if (niveau && evaluation.niveau !== niveau) return [];
        const lignes = (evaluation.eleves_reponses || []).filter((ligne) => cochees.has(ligne?.classe_nom));
        return lignes.length > 0 ? [{ ...evaluation, eleves_reponses: lignes }] : [];
    });
};
